import axios from 'axios'
import type { ApplyCaseMiddlewareOptions } from 'axios-case-converter'
import applyCaseMiddleware from 'axios-case-converter'
import { type AxiosRequestConfig } from 'axios'
import Cookies from 'js-cookie'
import { fetchAuthSession } from 'aws-amplify/auth'
import ROUTES from '../consts/paths'
import { CURRENT_ROLE_ID_KEY } from '../consts/roles'
import { translateUserFacingText } from './userFacingTerminology'

const options: ApplyCaseMiddlewareOptions = {
  caseOptions: {
    stripRegexp: /[^A-Z0-9]+/gi
  }
}

// Store all active AbortControllers
const activeControllers = new Set<AbortController>()

export function abortAllRequests (): void {
  for (const controller of activeControllers) {
    controller.abort()
  }
  activeControllers.clear()
}

const api = applyCaseMiddleware(axios.create({
  headers: {
    'Content-type': 'application/json',
    Accept: 'application/json'
  }
}), options)

api.interceptors.request.use(
  async config => {
    const controller = new AbortController()
    activeControllers.add(controller)
    // Remove controller when request finishes
    if (!config.signal) {
      config.signal = controller.signal
    }
    if (config.signal && typeof config.signal.addEventListener === 'function') {
      config.signal.addEventListener('abort', () => {
        activeControllers.delete(controller)
      })
    }
    config.headers['X-User-Role-id'] = localStorage.getItem(CURRENT_ROLE_ID_KEY)
    config.headers['Auth-Type'] = localStorage.getItem('authType')
    config.headers['Aws-Tenant-Key'] = localStorage.getItem('tenantKey')

    if (localStorage.getItem('authType') === 'cognito') {
      const authData = await fetchAuthSession()

      const token = authData?.tokens?.idToken

      if ((token === undefined || token === null) && !window.location.pathname.includes(ROUTES.LOGOUT)) {
        console.error('unable to find cognito token')
        controller.abort()
        window.location.href = `${window.location.origin}${ROUTES.LOGOUT}`
      } else {
        config.headers.Authorization = `Bearer ${token}`
      }
    }
    return {
      ...config,
      signal: controller.signal
    }
  },
  async error => {
    return error
  }
)

// https://medium.com/@sina.alizadeh120/repeating-failed-requests-after-token-refresh-in-axios-interceptors-for-react-js-apps-50feb54ddcbc

// Define the structure of a retry queue item
interface RetryQueueItem {
  resolve: (value?: any) => void
  reject: (error?: any) => void
  config: AxiosRequestConfig
}

// Create a list to hold the request queue
const refreshAndRetryQueue: RetryQueueItem[] = []

// Flag to prevent multiple token refresh requests
let isRefreshing = false
let tryRefresh = false

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // if we are trying a refresh and it still failed, we have failed completely
    if (tryRefresh) {
      tryRefresh = false
      throw error
    }

    const originalRequest: AxiosRequestConfig = error.config
    // originalRequest.transformResponse = undefined

    if (localStorage.getItem('authType') === 'edlink' &&
      Cookies.get('edlink_refresh_token_exists') != null &&
      error.response && error.response.status === 401) {
      if (!isRefreshing) {
        isRefreshing = true
        try {
          // Refresh the access token
          await axios.post('/api/auth/refresh')

          // Retry all requests in the queue with the new token
          console.log(refreshAndRetryQueue)
          refreshAndRetryQueue.forEach(({ config, resolve, reject }) => {
            api
              .request(config)
              .then((response) => { resolve(response) })
              .catch((err) => {
                reject(err)
              })
          })

          // Clear the queue
          refreshAndRetryQueue.length = 0

          tryRefresh = true
          const result = await api(originalRequest)

          // Retry the original request
          return result
        } catch (refreshError) {
          // Handle token refresh error
          // You can clear all storage and redirect the user to the login page
          window.location.href = `${window.location.origin}${ROUTES.LOGOUT}`
        } finally {
          isRefreshing = false
        }
      }

      // Add the original request to the queue
      return await new Promise((resolve, reject) => {
        refreshAndRetryQueue.push({ config: originalRequest, resolve, reject })
      })
    }

    // Return a Promise rejection if the status code is not 401
    const { status, data, config, code, stack } = error.response || {}

    const FALLBACK_MESSAGE = 'An unexpected error occurred. Please try again later.'

    const extractString = (value: unknown): string | null => {
      if (typeof value === 'string' && value.trim().length > 0) return value.trim()
      if (value && typeof value === 'object') {
        const obj = value as Record<string, unknown>
        for (const key of ['error', 'message', 'detail', 'description']) {
          if (typeof obj[key] === 'string' && (obj[key] as string).trim().length > 0) {
            return (obj[key] as string).trim()
          }
        }
      }
      return null
    }

    let message: string = FALLBACK_MESSAGE
    if (status === 401) {
      message = 'Unauthorized access. Please log in again.'
    } else if (data != null) {
      message = extractString(data) ?? extractString(data?.error) ?? FALLBACK_MESSAGE
    }

    const structuredError = {
      message: translateUserFacingText(message),
      // Preserve the raw data so callers that need release-date or other fields can still access it
      data: data ?? null,
      status,
      code,
      stack,
      config,
      isAxiosError: error.isAxiosError
    }

    return await Promise.reject(structuredError)
  }
)

export default api
